import React, { useState, useCallback } from "react";
import "./HobbliVision.css";

import WelcomePage from "./pages/WelcomePage";
import PreviewPage from "./pages/PreviewPage";
import ScanningPage from "./pages/ScanningPage";
import CandidatePage from "./pages/CandidatePage";
import ManualEntryPage from "./pages/ManualEntryPage";
import CardFormWrapper from "../../CardFormWrapper";
import useHobbliVision from "../hooks/hobblivision/useHobbliVision";

const STEPS = {
    WELCOME: "welcome",
    PREVIEW: "preview",
    SCANNING: "scanning",
    CANDIDATES: "candidates",
    MANUAL_ENTRY: "manual-entry",
    ADD_ITEM: "add-item",
};

export default function HobbliVisionModal({
    open,
    onClose,
    onItemConfirmed,
    user,
    onAddItem,
}) {
     console.log("HobbliVisionModal USER:", user);
    const [step, setStep] = useState(STEPS.WELCOME);
    const [imageFile, setImageFile] = useState(null);
    const [candidateItems, setCandidateItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [scanResult, setScanResult] = useState(null);

    const resetScanner = useCallback(() => {
        setStep(STEPS.WELCOME);
        setImageFile(null);
        setCandidateItems([]);
        setSelectedItem(null);
        setProcessedImage(null);
        setScanResult(null);
    }, []);

    const closeModal = () => {
        resetScanner();

        if (onClose) {
            onClose();
        }
    };

    const { scanImage } = useHobbliVision();

    /*
     * ============================================================
     * IMAGE SELECTION
     * ============================================================
     */

    const handleImageSelected = (file) => {
        setImageFile(file);
        setStep(STEPS.PREVIEW);
    };

    /*
     * ============================================================
     * RETAKE
     * ============================================================
     */

    const handleRetake = () => {
        setImageFile(null);
        setProcessedImage(null);
        setScanResult(null);
        setCandidateItems([]);
        setStep(STEPS.WELCOME);
    };

    /*
     * ============================================================
     * SCAN
     * ============================================================
     */

    const beginScan = async () => {
        if (!imageFile) {
            return;
        }

        setStep(STEPS.SCANNING);

        try {
            const results = await scanImage(imageFile);

            setScanResult(results);
            setProcessedImage(results?.processedImage || null);
            setCandidateItems(results?.candidates || []);
            setStep(STEPS.CANDIDATES);
        } catch (err) {
            console.error("HobbliVision scan failed:", err);

            setScanResult(null);
            setCandidateItems([]);
            setStep(STEPS.CANDIDATES);
        }
    };

    /*
     * ============================================================
     * CANDIDATE SELECTED
     * ============================================================
     */

    const confirmCandidate = (candidate) => {
        console.log("HobbliVision candidate selected:", candidate);

        const item = candidate?.item || candidate;
        const parsed = scanResult?.parsedResult || {};

        const confirmedCardData = {
            /*
            * Start with the matched database item.
            * This gives us the cleanest known information.
            */
            ...item,

            /*
            * CardForm expects suggested_name for the
            * user-facing item name.
            */
            suggested_name:
                item?.name ||
                item?.user_specific_name ||
                candidate?.itemName ||
                candidate?.user_specific_name ||
                "",

            /*
            * Prefer the matched database value.
            * Fall back to parser evidence if the database
            * doesn't have the field.
            */
            year: item?.year ?? item?.user_specific_year ?? "",
            brand: item?.brand || item?.user_specific_brand || "",
            set_name: item?.set_name || item?.user_specific_set_name || "",
            card_number: item?.card_number || item?.attributes?.card_number || "",
            category_id: item?.category_id || "",
            subcategory_id: item?.subcategory_id || "",
            parallel: item?.parallel || "" || item?.attributes?.parallel || "",
            variations: item?.variations || [] || item?.attributes?.variations || [],
            autographed: item?.additional_details?.autographed || item?.attributes?.autographed || false,
            graded: item?.additional_details?.graded || item?.attributes?.graded || false,

            /*
            * Preserve the HobbliVision information in case
            * we want to use it later.
            */
            hobbliVision: {
                scanResult,
                imageFile,
                processedImage,
                candidate,
            },
        };

        console.log(
            "HobbliVision card data for Add Card:",
            confirmedCardData
        );

        setSelectedItem(confirmedCardData);
        setStep(STEPS.ADD_ITEM);
    };

    /*
     * ============================================================
     * MANUAL ENTRY
     *
     * Preserve useful parser information even when there is no
     * exact database match.
     * ============================================================
     */

    const openManualEntry = () => {
        const parsed = scanResult?.parsedResult || {};

        const resolvedName =
            scanResult?.matchResult?.resolvedNameEvidence
                ?.primary?.value ||
            null;

        const manualCardData = {
            suggested_name:
                resolvedName ||
                parsed.player ||
                parsed.itemName ||
                "",

            year:
                parsed.year ||
                "",

            brand:
                parsed.brand ||
                "",

            set_name:
                parsed.set ||
                "",

            card_number:
                parsed.cardNumber ||
                "",

            category_id: "",
            subcategory_id: "",

            hobbliVision: {
                scanResult,
                imageFile,
                processedImage,
            },
        };

        setSelectedItem(manualCardData);
        setStep(STEPS.ADD_ITEM);
    };

    /*
     * ============================================================
     * ITEM SAVED
     * ============================================================
     */

    const handleItemAdded = (newCard) => {
        console.log("HobbliVision item added:", newCard);

        if (onAddItem) {
            onAddItem(newCard);
        }

        if (onItemConfirmed) {
            onItemConfirmed(newCard);
        }

        closeModal();
    };

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    if (!open) {
        return null;
    }

    return (
        <div className="hv-root">
            <div
                className="hv-overlay"
                onClick={closeModal}
            >
                <div
                    className="hv-modal"
                    onClick={(event) => event.stopPropagation()}
                >

                    {/* ====================================================
                        HEADER
                    ==================================================== */}

                    <div className="hv-header">
                        <div>
                            <div className="hv-title">
                                ✨ HobbliVision
                            </div>

                            <div className="hv-subtitle">
                                Recognize. Identify. Collect.
                            </div>
                        </div>

                        <button
                            type="button"
                            className="hv-close-button"
                            onClick={closeModal}
                            aria-label="Close HobbliVision"
                        >
                            ✕
                        </button>
                    </div>

                    {/* ====================================================
                        BODY
                    ==================================================== */}

                    <div className="hv-body">

                        {/* ============================
                            WELCOME
                        ============================ */}

                        {step === STEPS.WELCOME && (
                            <WelcomePage
                                onImageSelected={handleImageSelected}
                            />
                        )}

                        {/* ============================
                            PREVIEW
                        ============================ */}

                        {step === STEPS.PREVIEW && (
                            <PreviewPage
                                imageFile={imageFile}
                                onRetake={handleRetake}
                                onContinue={beginScan}
                            />
                        )}

                        {/* ============================
                            SCANNING
                        ============================ */}

                        {step === STEPS.SCANNING && (
                            <ScanningPage />
                        )}

                        {/* ============================
                            CANDIDATES
                        ============================ */}

                        {step === STEPS.CANDIDATES && (
                            <CandidatePage
                                candidates={candidateItems}
                                scanResult={scanResult}
                                onSelect={confirmCandidate}
                                onBack={handleRetake}
                                onManualEntry={openManualEntry}
                            />
                        )}

                        {/* ============================
                            ADD ITEM
                        ============================ */}

                        {step === STEPS.ADD_ITEM && (
                            <CardFormWrapper
                                user={user}
                                cardData={selectedItem || {}}
                                isEdit={false}
                                onAddItem={handleItemAdded}
                                onCancel={() => {
                                    setStep(STEPS.CANDIDATES);
                                }}
                            />
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
}

